import type { IQuoteSearchParams } from '@/interfaces'

/**
 * QuoteSearchParams model matching API QuoteSearchParams
 * Provides simple parameters to limit quote data scope
 */
export class QuoteSearchParams implements IQuoteSearchParams {
	// Core scope limiters
	subjobId?: number | null
	jobId?: number | null
	clientId?: string | null

	// Row Level Security filter (optional)
	salesGroup?: number[] | null

	// Date range limiter
	dateFrom?: Date | string | null
	dateTo?: Date | string | null

	// Status filter
	activeOnly: boolean

	// Safety limit - optional, backend will apply reasonable default if not specified
	maxRecords?: number | null

	constructor({
		subjobId,
		jobId,
		clientId,
		salesGroup,
		dateFrom,
		dateTo,
		activeOnly = true,
		maxRecords
	}: Partial<IQuoteSearchParams> = {}) {
		// Core scope limiters
		this.subjobId = subjobId ?? null
		this.jobId = jobId ?? null
		this.clientId = clientId ?? null

		// Row Level Security filter
		this.salesGroup = salesGroup ?? null

		// Date range limiter - parse if string provided
		this.dateFrom = dateFrom ? this.parseDate(dateFrom) : null
		this.dateTo = dateTo ? this.parseDate(dateTo) : null

		// Status filter - default to true
		this.activeOnly = activeOnly

		// Safety limit
		this.maxRecords = maxRecords ?? null
	}

	/**
	 * Parse a date string or Date object to ensure Date or string format
	 */
	private parseDate(date: Date | string | null | undefined): Date | string | null {
		if (!date) return null
		if (date instanceof Date) return date
		// If it's a string, validate it's a valid date
		const parsed = new Date(date)
		if (isNaN(parsed.getTime())) {
			// If invalid date, return as string (let backend handle it)
			return date
		}
		return parsed
	}

	/**
	 * Convert to plain object for API request
	 * Filters out null/undefined values
	 */
	toApiParams(): Record<string, unknown> {
		const params: Record<string, unknown> = {}

		if (this.subjobId !== null && this.subjobId !== undefined) {
			params.subjob_id = this.subjobId
		}
		if (this.jobId !== null && this.jobId !== undefined) {
			params.job_id = this.jobId
		}
		if (this.clientId !== null && this.clientId !== undefined) {
			params.client_id = this.clientId
		}
		if (this.salesGroup !== null && this.salesGroup !== undefined && this.salesGroup.length > 0) {
			params.sales_group = this.salesGroup
		}
		if (this.dateFrom !== null && this.dateFrom !== undefined) {
			params.date_from = this.dateFrom instanceof Date ? this.dateFrom.toISOString().split('T')[0] : this.dateFrom
		}
		if (this.dateTo !== null && this.dateTo !== undefined) {
			params.date_to = this.dateTo instanceof Date ? this.dateTo.toISOString().split('T')[0] : this.dateTo
		}
		if (this.activeOnly !== undefined) {
			params.active_only = this.activeOnly
		}
		if (this.maxRecords !== null && this.maxRecords !== undefined) {
			params.max_records = this.maxRecords
		}

		return params
	}

	/**
	 * Check if any filters are applied
	 */
	hasFilters(): boolean {
		return !!(
			this.subjobId ||
			this.jobId ||
			this.clientId ||
			(this.salesGroup && this.salesGroup.length > 0) ||
			this.dateFrom ||
			this.dateTo ||
			this.maxRecords ||
			!this.activeOnly // When false, it's a filter (showing inactive)
		)
	}

	/**
	 * Clear all filters to default state
	 */
	clearFilters(): void {
		this.subjobId = null
		this.jobId = null
		this.clientId = null
		this.salesGroup = null
		this.dateFrom = null
		this.dateTo = null
		this.activeOnly = true
		this.maxRecords = null
	}
}
